import React, { useState } from 'react';
import {
  Layers,
  Clock,
  Database,
  Plus,
  Edit2,
  Trash2,
  X,
  BookOpen,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { AcademicLevel, AcademicBatch, MasterEntry, AdminRole, LevelSyllabusItem, SyllabusLearningType } from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

interface AcademicStructureProps {
  levels: AcademicLevel[];
  batches: AcademicBatch[];
  masters: MasterEntry[];
  currentRole: AdminRole;
  onAddLevel: (lvl: AcademicLevel) => void;
  onUpdateLevel: (lvl: AcademicLevel) => void;
  onAddBatch: (bat: AcademicBatch) => void;
  onUpdateBatch: (bat: AcademicBatch) => void;
  onAddMaster: (mst: MasterEntry) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const getLearningTypeBadgeVariant = (
  type: SyllabusLearningType
): 'soft' | 'accent' | 'warning' | 'success' | 'outline' => {
  switch (type) {
    case 'Sutra':
      return 'soft';
    case 'Stavan':
      return 'accent';
    case 'Story':
      return 'warning';
    case 'Activity':
      return 'success';
    default:
      return 'outline';
  }
};

export const AcademicStructure: React.FC<AcademicStructureProps> = ({
  levels,
  batches,
  masters,
  currentRole,
  onAddLevel,
  onUpdateLevel,
  onAddBatch,
  onUpdateBatch,
  onAddMaster,
  addToast,
}) => {
  const [subTab, setSubTab] = useState<'levels' | 'batches' | 'masters'>('levels');

  const isAuditor = currentRole === 'Auditor';

  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<AcademicLevel | null>(null);
  const [levelForm, setLevelForm] = useState({
    code: '',
    name: '',
    description: '',
    minAge: 5,
    maxAge: 10,
    prerequisite: 'None',
  });

  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<AcademicBatch | null>(null);
  const [batchForm, setBatchForm] = useState({
    code: '',
    timeSlot: '',
    startTime: '09:00 AM',
    endTime: '10:30 AM',
    maxCapacity: 50,
    operatingDays: ['Saturday', 'Sunday'],
  });

  const [isMasterModalOpen, setIsMasterModalOpen] = useState(false);
  const [masterForm, setMasterForm] = useState({
    category: 'Cities' as 'Cities' | 'Sangh' | 'Relationships' | 'Documents',
    value: '',
  });

  const [selectedLevelForSyllabus, setSelectedLevelForSyllabus] = useState<AcademicLevel | null>(null);
  const [levelDetailTab, setLevelDetailTab] = useState<'syllabus' | 'general'>('syllabus');

  const [syllabusSearch, setSyllabusSearch] = useState('');
  const [learningTypeFilter, setLearningTypeFilter] = useState<string>('ALL');

  const [isSyllabusItemModalOpen, setIsSyllabusItemModalOpen] = useState(false);
  const [editingSyllabusItem, setEditingSyllabusItem] = useState<LevelSyllabusItem | null>(null);
  const [syllabusItemForm, setSyllabusItemForm] = useState({
    chapterName: '',
    description: '',
    learningType: 'Sutra' as SyllabusLearningType,
    displayOrder: 1,
    status: 'Active' as 'Active' | 'Inactive',
    referenceTopic: '',
  });

  const openAddLevel = () => {
    setEditingLevel(null);
    setLevelForm({
      code: `LEVEL-${levels.length + 1}`,
      name: '',
      description: '',
      minAge: 5,
      maxAge: 12,
      prerequisite: 'None',
    });
    setIsLevelModalOpen(true);
  };

  const openEditLevel = (lvl: AcademicLevel) => {
    if (isAuditor) return;
    setEditingLevel(lvl);
    setLevelForm({
      code: lvl.code,
      name: lvl.name,
      description: lvl.description,
      minAge: lvl.minAge,
      maxAge: lvl.maxAge,
      prerequisite: lvl.prerequisite,
    });
    setIsLevelModalOpen(true);
  };

  const openLevelSyllabus = (lvl: AcademicLevel) => {
    setSelectedLevelForSyllabus(lvl);
    setLevelDetailTab('syllabus');
    setSyllabusSearch('');
    setLearningTypeFilter('ALL');
  };

  const handleLevelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    if (editingLevel) {
      const updated = {
        ...editingLevel,
        ...levelForm,
      };
      onUpdateLevel(updated);
      if (selectedLevelForSyllabus?.id === editingLevel.id) {
        setSelectedLevelForSyllabus(updated);
      }
      addToast('success', 'Level Updated', `Updated curriculum details for ${levelForm.name}`);
    } else {
      const newLvl: AcademicLevel = {
        id: `LVL-${Math.floor(10 + Math.random() * 90)}`,
        ...levelForm,
        enrolledCount: 0,
        syllabus: [],
      };
      onAddLevel(newLvl);
      addToast('success', 'Level Created', `Created level ${newLvl.name}`);
    }
    setIsLevelModalOpen(false);
  };

  const openAddBatch = () => {
    setEditingBatch(null);
    setBatchForm({
      code: `BATCH-${String.fromCharCode(65 + batches.length)}`,
      timeSlot: '',
      startTime: '09:00 AM',
      endTime: '10:30 AM',
      maxCapacity: 50,
      operatingDays: ['Saturday', 'Sunday'],
    });
    setIsBatchModalOpen(true);
  };

  const openEditBatch = (bat: AcademicBatch) => {
    if (isAuditor) return;
    setEditingBatch(bat);
    setBatchForm({
      code: bat.code,
      timeSlot: bat.timeSlot,
      startTime: bat.startTime,
      endTime: bat.endTime,
      maxCapacity: bat.maxCapacity,
      operatingDays: [...bat.operatingDays],
    });
    setIsBatchModalOpen(true);
  };

  const handleBatchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    if (editingBatch) {
      onUpdateBatch({
        ...editingBatch,
        ...batchForm,
      });
      addToast('success', 'Batch Updated', `Updated slot ${batchForm.timeSlot}`);
    } else {
      const newBat: AcademicBatch = {
        id: `BAT-${Math.floor(10 + Math.random() * 90)}`,
        ...batchForm,
        enrolledCount: 0,
      };
      onAddBatch(newBat);
      addToast('success', 'Batch Created', `Created batch slot ${newBat.timeSlot}`);
    }
    setIsBatchModalOpen(false);
  };

  const handleMasterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor || !masterForm.value.trim()) return;

    const newMst: MasterEntry = {
      id: `MST-${Math.floor(100 + Math.random() * 900)}`,
      category: masterForm.category,
      value: masterForm.value.trim(),
      status: 'Active',
    };
    onAddMaster(newMst);
    addToast('success', 'Master Created', `Added ${newMst.value} under ${newMst.category}`);
    setMasterForm({ ...masterForm, value: '' });
    setIsMasterModalOpen(false);
  };

  const activeSyllabusList = selectedLevelForSyllabus?.syllabus || [];

  const filteredSyllabusItems = activeSyllabusList.filter((item) => {
    const matchesSearch =
      item.chapterName.toLowerCase().includes(syllabusSearch.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(syllabusSearch.toLowerCase()));
    const matchesType = learningTypeFilter === 'ALL' || item.learningType === learningTypeFilter;
    return matchesSearch && matchesType;
  });

  const handleMoveSyllabusItem = (index: number, direction: 'up' | 'down') => {
    if (!selectedLevelForSyllabus || isAuditor) return;
    const currentSyllabus = [...(selectedLevelForSyllabus.syllabus || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= currentSyllabus.length) return;

    const temp = currentSyllabus[index];
    currentSyllabus[index] = currentSyllabus[targetIndex];
    currentSyllabus[targetIndex] = temp;

    const reordered = currentSyllabus.map((item, i) => ({
      ...item,
      displayOrder: i + 1,
    }));

    const updatedLevel: AcademicLevel = {
      ...selectedLevelForSyllabus,
      syllabus: reordered,
    };

    setSelectedLevelForSyllabus(updatedLevel);
    onUpdateLevel(updatedLevel);
    addToast('success', 'Sequence Updated', `Reordered syllabus topics for ${selectedLevelForSyllabus.name}`);
  };

  const openAddSyllabusItem = () => {
    setEditingSyllabusItem(null);
    const nextOrder = (selectedLevelForSyllabus?.syllabus?.length || 0) + 1;
    setSyllabusItemForm({
      chapterName: '',
      description: '',
      learningType: 'Sutra',
      displayOrder: nextOrder,
      status: 'Active',
      referenceTopic: '',
    });
    setIsSyllabusItemModalOpen(true);
  };

  const openEditSyllabusItem = (item: LevelSyllabusItem) => {
    if (isAuditor) return;
    setEditingSyllabusItem(item);
    setSyllabusItemForm({
      chapterName: item.chapterName,
      description: item.description || '',
      learningType: item.learningType,
      displayOrder: item.displayOrder,
      status: item.status,
      referenceTopic: item.referenceTopic || '',
    });
    setIsSyllabusItemModalOpen(true);
  };

  const handleRemoveSyllabusItem = (itemId: string) => {
    if (!selectedLevelForSyllabus || isAuditor) return;
    const updatedSyllabus = (selectedLevelForSyllabus.syllabus || [])
      .filter((item) => item.id !== itemId)
      .map((item, idx) => ({ ...item, displayOrder: idx + 1 }));

    const updatedLevel: AcademicLevel = {
      ...selectedLevelForSyllabus,
      syllabus: updatedSyllabus,
    };

    setSelectedLevelForSyllabus(updatedLevel);
    onUpdateLevel(updatedLevel);
    addToast('success', 'Item Removed', 'Syllabus item removed from level.');
  };

  const handleSyllabusItemSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLevelForSyllabus || isAuditor) return;

    const currentSyllabus = [...(selectedLevelForSyllabus.syllabus || [])];

    if (editingSyllabusItem) {
      const updated = currentSyllabus.map((item) =>
        item.id === editingSyllabusItem.id
          ? {
              ...item,
              ...syllabusItemForm,
            }
          : item
      );
      updated.sort((a, b) => a.displayOrder - b.displayOrder);

      const updatedLevel: AcademicLevel = {
        ...selectedLevelForSyllabus,
        syllabus: updated,
      };
      setSelectedLevelForSyllabus(updatedLevel);
      onUpdateLevel(updatedLevel);
      addToast('success', 'Syllabus Item Updated', `Updated topic "${syllabusItemForm.chapterName}"`);
    } else {
      const newItem: LevelSyllabusItem = {
        id: `SYL-${Date.now()}`,
        levelId: selectedLevelForSyllabus.id,
        ...syllabusItemForm,
      };
      const updated = [...currentSyllabus, newItem];
      updated.sort((a, b) => a.displayOrder - b.displayOrder);

      const updatedLevel: AcademicLevel = {
        ...selectedLevelForSyllabus,
        syllabus: updated,
      };
      setSelectedLevelForSyllabus(updatedLevel);
      onUpdateLevel(updatedLevel);
      addToast('success', 'Syllabus Item Created', `Added "${newItem.chapterName}" to ${selectedLevelForSyllabus.name}`);
    }

    setIsSyllabusItemModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--gv-ink)] tracking-tight">Academic Structure & Masters</h1>
          <p className="text-xs text-[var(--gv-ink-muted)] mt-0.5">Configure curriculum levels, assigned syllabus topics, batch timing slots, and system master lookups.</p>
        </div>

        <div className="flex items-center rounded-xl border border-[var(--gv-border)] bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setSubTab('levels')}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              subTab === 'levels' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Levels ({levels.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setSubTab('batches')}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              subTab === 'batches' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Batches ({batches.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setSubTab('masters')}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              subTab === 'masters' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            <Database className="w-3.5 h-3.5" />
            <span>Masters ({masters.length})</span>
          </button>
        </div>
      </div>

      {subTab === 'levels' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-[var(--gv-ink)]">Curriculum Levels Config</h2>
            {!isAuditor && (
              <Button leftIcon={Plus} onClick={openAddLevel}>
                Create Level
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {levels.map((lvl) => (
              <div
                key={lvl.id}
                className="admin-card p-5 space-y-4 flex flex-col justify-between hover:border-[var(--gv-border)] transition-all"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold text-[var(--gv-brand)]">{lvl.code}</span>
                    <Badge variant="accent" className="font-mono normal-case">
                      {lvl.enrolledCount} Enrolled
                    </Badge>
                  </div>
                  <h3 className="text-base font-bold text-[var(--gv-ink)]">{lvl.name}</h3>
                  <p className="text-xs text-[var(--gv-ink-muted)] leading-relaxed font-normal">{lvl.description}</p>
                </div>

                <div className="bg-[var(--gv-surface)] p-3 rounded-lg border border-[var(--gv-border)]/80 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between font-mono">
                    <span className="text-[var(--gv-ink-muted)]">Age Bracket:</span>
                    <span className="font-bold text-[var(--gv-ink)]">{lvl.minAge} - {lvl.maxAge} Yrs</span>
                  </div>
                  <div className="flex items-center justify-between font-mono">
                    <span className="text-[var(--gv-ink-muted)]">Prerequisite:</span>
                    <span className="font-bold text-[var(--gv-ink)]">{lvl.prerequisite}</span>
                  </div>
                  <div className="flex items-center justify-between font-mono pt-1.5 border-t border-[var(--gv-border)]/80">
                    <span className="text-[var(--gv-ink-muted)]">Assigned Syllabus:</span>
                    <span className="font-bold text-[var(--gv-brand)] flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5 text-[var(--gv-brand)]" />
                      {lvl.syllabus?.length || 0} Topics
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t border-stone-100 flex items-center justify-between gap-2">
                  <Button
                    leftIcon={BookOpen}
                    onClick={() => openLevelSyllabus(lvl)}
                    className="flex-1"
                  >
                    Assigned Syllabus ({lvl.syllabus?.length || 0})
                  </Button>

                  {!isAuditor && (
                    <Button variant="outline" size="sm" leftIcon={Edit2} onClick={() => openEditLevel(lvl)}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === 'batches' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-[var(--gv-ink)]">Batch Slot Schedules</h2>
            {!isAuditor && (
              <Button leftIcon={Plus} onClick={openAddBatch}>
                Create Batch Slot
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {batches.map((bat) => {
              const utilizationPct = Math.round((bat.enrolledCount / bat.maxCapacity) * 100);

              return (
                <div
                  key={bat.id}
                  className="admin-card p-5 space-y-4 flex flex-col justify-between hover:border-[var(--gv-border)] transition-all"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono font-bold text-[var(--gv-brand)]">{bat.code}</span>
                      <span className="text-xs font-mono font-bold text-emerald-700">
                        {bat.enrolledCount} / {bat.maxCapacity} Seats ({utilizationPct}%)
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-[var(--gv-ink)]">{bat.timeSlot}</h3>
                    <p className="text-xs text-[var(--gv-ink-muted)] font-mono font-semibold">
                      ⏰ {bat.startTime} - {bat.endTime}
                    </p>
                  </div>

                  <div className="bg-[var(--gv-surface)] p-3 rounded-lg border border-[var(--gv-border)]/80 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase">Operating Days</span>
                      <span className="font-bold text-[var(--gv-ink)]">{bat.operatingDays.join(', ')}</span>
                    </div>
                    <div className="w-full bg-stone-200 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-[var(--gv-brand)] h-full rounded-full transition-all"
                        style={{ width: `${utilizationPct}%` }}
                      />
                    </div>
                  </div>

                  {!isAuditor && (
                    <div className="pt-2 border-t border-stone-100 flex justify-end">
                      <Button variant="outline" size="sm" leftIcon={Edit2} onClick={() => openEditBatch(bat)}>
                        Edit Slot
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {subTab === 'masters' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-[var(--gv-ink)]">System Lookup Master Tables</h2>
            {!isAuditor && (
              <Button leftIcon={Plus} onClick={() => setIsMasterModalOpen(true)}>
                Add Master Entry
              </Button>
            )}
          </div>

          <div className="admin-card overflow-hidden">
            <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
              <thead className="bg-[var(--gv-surface)] text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase tracking-wider border-b border-[var(--gv-border)]">
                <tr>
                  <th className="py-2.5 px-4 font-bold">Master ID</th>
                  <th className="py-2.5 px-4 font-bold">Category</th>
                  <th className="py-2.5 px-4 font-bold">Value</th>
                  <th className="py-2.5 px-4 font-bold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium bg-white">
                {masters.map((m) => (
                  <tr key={m.id} className="hover:bg-[var(--gv-surface)]/80 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-[var(--gv-brand)]">{m.id}</td>
                    <td className="py-3 px-4 text-[var(--gv-ink-muted)] font-semibold">{m.category}</td>
                    <td className="py-3 px-4 font-bold text-[var(--gv-ink)]">{m.value}</td>
                    <td className="py-3 px-4">
                      <Badge variant="success" className="font-mono normal-case">
                        {m.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedLevelForSyllabus && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="admin-card max-w-4xl w-full p-6 shadow-2xl space-y-5 text-[var(--gv-ink)] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-[var(--gv-border)] flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[var(--gv-brand-soft)] rounded-xl text-[var(--gv-brand)]">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="soft" className="font-mono normal-case">
                      {selectedLevelForSyllabus.code}
                    </Badge>
                    <h3 className="text-lg font-bold text-[var(--gv-ink)]">{selectedLevelForSyllabus.name}</h3>
                  </div>
                  <p className="text-xs text-[var(--gv-ink-muted)] mt-0.5">{selectedLevelForSyllabus.description}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setSelectedLevelForSyllabus(null)}
              />
            </div>

            <div className="flex items-center justify-between bg-stone-100 p-1 rounded-xl border border-[var(--gv-border)] flex-shrink-0">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setLevelDetailTab('syllabus')}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
                    levelDetailTab === 'syllabus' ? 'admin-tab-active' : 'admin-tab-idle'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Assigned Syllabus ({selectedLevelForSyllabus.syllabus?.length || 0})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLevelDetailTab('general')}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
                    levelDetailTab === 'general' ? 'admin-tab-active' : 'admin-tab-idle'
                  }`}
                >
                  <Layers className="w-4 h-4" />
                  <span>Level Configuration</span>
                </button>
              </div>

              {levelDetailTab === 'syllabus' && !isAuditor && (
                <Button leftIcon={Plus} onClick={openAddSyllabusItem}>
                  Add Syllabus Item
                </Button>
              )}
            </div>

            {levelDetailTab === 'syllabus' && (
              <div className="space-y-4 flex-1 overflow-y-auto pr-1">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-[var(--gv-surface)] p-3 rounded-xl border border-[var(--gv-border)]">
                  <Search
                    value={syllabusSearch}
                    onChange={setSyllabusSearch}
                    placeholder="Search chapter or topic..."
                    containerClassName="w-full sm:w-64"
                  />

                  <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
                    <span className="text-[11px] font-mono text-[var(--gv-ink-muted)] font-bold whitespace-nowrap">Learning Type:</span>
                    {(['ALL', 'Sutra', 'Stavan', 'Story', 'Activity', 'Other'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setLearningTypeFilter(type)}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                          learningTypeFilter === type ? 'admin-tab-active' : 'admin-tab-idle bg-white border border-[var(--gv-border)]'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredSyllabusItems.length === 0 ? (
                  <div className="text-center py-12 bg-[var(--gv-surface)] rounded-xl border border-dashed border-[var(--gv-border)] space-y-2">
                    <BookOpen className="w-8 h-8 text-[var(--gv-ink-muted)] mx-auto" />
                    <p className="text-sm font-bold text-[var(--gv-ink)]">No syllabus items found</p>
                    <p className="text-xs text-[var(--gv-ink-muted)]">
                      {selectedLevelForSyllabus.syllabus?.length === 0
                        ? 'No chapters or topics assigned to this level yet.'
                        : 'Try adjusting your search query or learning type filter.'}
                    </p>
                    {!isAuditor && (
                      <Button leftIcon={Plus} onClick={openAddSyllabusItem} className="mt-2">
                        Add First Syllabus Item
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center px-1 text-[11px] font-mono text-[var(--gv-ink-muted)] font-bold uppercase">
                      <span>Order # & Topic ({filteredSyllabusItems.length} Items)</span>
                      <span>Sequence Controls & Actions</span>
                    </div>

                    <div className="divide-y divide-stone-100 bg-white rounded-xl border border-[var(--gv-border)]/90 shadow-2xs overflow-hidden">
                      {filteredSyllabusItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="p-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-[var(--gv-surface)]/80 transition-colors"
                        >
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <span className="w-7 h-7 rounded-lg bg-[var(--gv-surface)] border border-[var(--gv-border)]/80 font-mono text-xs font-bold text-[var(--gv-brand)] flex items-center justify-center flex-shrink-0">
                              {item.displayOrder}
                            </span>

                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-xs font-bold text-[var(--gv-ink)]">{item.chapterName}</h4>
                                <Badge variant={getLearningTypeBadgeVariant(item.learningType)} className="font-mono normal-case">
                                  {item.learningType}
                                </Badge>
                                <Badge variant={item.status === 'Active' ? 'success' : 'outline'} className="font-mono normal-case">
                                  {item.status}
                                </Badge>
                              </div>

                              {item.description && (
                                <p className="text-xs text-[var(--gv-ink-muted)] line-clamp-2 leading-relaxed">{item.description}</p>
                              )}

                              {item.referenceTopic && (
                                <span className="inline-block text-[10px] font-mono text-[var(--gv-ink-muted)]">
                                  Ref: {item.referenceTopic}
                                </span>
                              )}
                            </div>
                          </div>

                          {!isAuditor && (
                            <div className="flex items-center gap-1.5 self-end md:self-center flex-shrink-0">
                              <Tooltip content="Move Up">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  leftIcon={ArrowUp}
                                  disabled={index === 0}
                                  aria-label="Move Up"
                                  onClick={() => handleMoveSyllabusItem(index, 'up')}
                                />
                              </Tooltip>

                              <Tooltip content="Move Down">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  leftIcon={ArrowDown}
                                  disabled={index === filteredSyllabusItems.length - 1}
                                  aria-label="Move Down"
                                  onClick={() => handleMoveSyllabusItem(index, 'down')}
                                />
                              </Tooltip>

                              <Tooltip content="Edit Topic">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  leftIcon={Edit2}
                                  onClick={() => openEditSyllabusItem(item)}
                                >
                                  Edit
                                </Button>
                              </Tooltip>

                              <Tooltip content="Remove Topic">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  leftIcon={Trash2}
                                  aria-label="Remove Topic"
                                  className="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                  onClick={() => handleRemoveSyllabusItem(item.id)}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {levelDetailTab === 'general' && (
              <div className="bg-[var(--gv-surface)] p-5 rounded-xl border border-[var(--gv-border)] space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase font-bold block">Level Code</span>
                    <span className="text-[var(--gv-ink)] font-bold text-sm font-mono">{selectedLevelForSyllabus.code}</span>
                  </div>
                  <div>
                    <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase font-bold block">Enrolled Students</span>
                    <span className="text-[var(--gv-ink)] font-bold text-sm">{selectedLevelForSyllabus.enrolledCount} Students</span>
                  </div>
                  <div>
                    <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase font-bold block">Age Bracket</span>
                    <span className="text-[var(--gv-ink)] font-bold">{selectedLevelForSyllabus.minAge} - {selectedLevelForSyllabus.maxAge} Years</span>
                  </div>
                  <div>
                    <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase font-bold block">Prerequisite</span>
                    <span className="text-[var(--gv-ink)] font-bold">{selectedLevelForSyllabus.prerequisite}</span>
                  </div>
                </div>
                <div>
                  <span className="text-[var(--gv-ink-muted)] font-mono text-[10px] uppercase font-bold block mb-1">Description</span>
                  <p className="text-[var(--gv-ink-muted)] bg-white p-3 rounded-lg border border-[var(--gv-border)] leading-relaxed">
                    {selectedLevelForSyllabus.description}
                  </p>
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-[var(--gv-border)] flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-[var(--gv-ink-muted)] font-mono">
                Total Assigned Syllabus: <strong className="text-[var(--gv-ink)]">{selectedLevelForSyllabus.syllabus?.length || 0} Topics</strong>
              </span>
              <Button variant="secondary" onClick={() => setSelectedLevelForSyllabus(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {isSyllabusItemModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-lg w-full p-6 shadow-2xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingSyllabusItem ? 'Edit Syllabus Item' : 'Add New Syllabus Item'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsSyllabusItemModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSyllabusItemSubmit} className="space-y-3.5 text-xs">
              <Input
                label="Chapter / Topic Name *"
                required
                value={syllabusItemForm.chapterName}
                onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, chapterName: e.target.value })}
                placeholder="e.g., Navkar Mantra - Part 1: Pronunciation"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Learning Type</label>
                  <select
                    value={syllabusItemForm.learningType}
                    onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, learningType: e.target.value as SyllabusLearningType })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Sutra">Sutra</option>
                    <option value="Stavan">Stavan</option>
                    <option value="Story">Story</option>
                    <option value="Activity">Activity</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <Input
                  label="Display Order"
                  type="number"
                  min={1}
                  required
                  value={syllabusItemForm.displayOrder}
                  onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, displayOrder: Math.max(1, parseInt(e.target.value) || 1) })}
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Status</label>
                <select
                  value={syllabusItemForm.status}
                  onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, status: e.target.value as 'Active' | 'Inactive' })}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description (Optional)</label>
                <textarea
                  rows={3}
                  value={syllabusItemForm.description}
                  onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, description: e.target.value })}
                  placeholder="Brief overview of learning objectives, verses, or key concepts..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <Input
                label="Reference / Topic Tag (Optional)"
                value={syllabusItemForm.referenceTopic}
                onChange={(e) => setSyllabusItemForm({ ...syllabusItemForm, referenceTopic: e.target.value })}
                placeholder="e.g., Kalpa Sutra Chapter 2 / Gatha 1-5"
              />

              <div className="pt-3 flex justify-end gap-2 border-t border-stone-100">
                <Button type="button" variant="secondary" onClick={() => setIsSyllabusItemModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Syllabus Item</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLevelModalOpen && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-md w-full p-6 shadow-xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingLevel ? `Edit Level: ${editingLevel.code}` : 'Create Academic Level'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsLevelModalOpen(false)}
              />
            </div>

            <form onSubmit={handleLevelSubmit} className="space-y-3 text-xs">
              <Input
                label="Level Name"
                required
                value={levelForm.name}
                onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                placeholder="e.g., Level 4 - Praveen"
              />

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description</label>
                <textarea
                  rows={2}
                  required
                  value={levelForm.description}
                  onChange={(e) => setLevelForm({ ...levelForm, description: e.target.value })}
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Min Age"
                  type="number"
                  required
                  value={levelForm.minAge}
                  onChange={(e) => setLevelForm({ ...levelForm, minAge: Number(e.target.value) })}
                />
                <Input
                  label="Max Age"
                  type="number"
                  required
                  value={levelForm.maxAge}
                  onChange={(e) => setLevelForm({ ...levelForm, maxAge: Number(e.target.value) })}
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setIsLevelModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Level Config</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isMasterModalOpen && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-sm w-full p-6 shadow-xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Add Master Entry</h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsMasterModalOpen(false)}
              />
            </div>

            <form onSubmit={handleMasterSubmit} className="space-y-3 text-xs">
              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Category</label>
                <select
                  value={masterForm.category}
                  onChange={(e) => setMasterForm({ ...masterForm, category: e.target.value as any })}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="Cities">Cities</option>
                  <option value="Sangh">Sangh / Temple</option>
                  <option value="Relationships">Relationships</option>
                  <option value="Documents">Documents</option>
                </select>
              </div>

              <Input
                label="Master Value"
                required
                value={masterForm.value}
                onChange={(e) => setMasterForm({ ...masterForm, value: e.target.value })}
                placeholder="e.g., Borivali Sangh"
              />

              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setIsMasterModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Entry</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
